import { OrderStatus } from "@/types/enum";
import { OrderItemResult } from "@/types/api";
import DateHelper from "@/utils/dateHelper";

const INTEGRAL_ARRIVAL_DAY_LABEL: Record<number, string> = { 0: "今天", 1: "明天", 2: "后天" };

/**
 * 下单返奖励金到账状态
 * - none：不展示（无返奖励金 / 兑换成功订单 / 非付款成功或已发货状态 / 终态但未开启 showTerminalStateArrival）
 * - arrived：已到账（开启 showTerminalStateArrival 时的交易成功 / 客服提前申请到账 / 已过15天到账时间）
 * - returned：已退回（开启 showTerminalStateArrival 时的交易关闭）
 * - pending：到账倒计时中，dayLabel 为今天/明天/后天（超过后天则为空，此时用 dayDiff 天数展示）
 */
export type IntegralArrivalState = { type: "none" } | { type: "arrived" } | { type: "returned" } | { type: "pending"; dayLabel?: string; time: string; dayDiff: number };

export type IntegralArrivalOptions = {
  /**
   * 是否展示交易成功/交易关闭等终态的到账结果
   * - 订单详情页传 true：交易成功展示已到账、交易关闭展示已退回
   * - 订单列表页传 false（默认）：终态不展示
   */
  showTerminalStateArrival?: boolean;
};

const OrderStatusLabel: Record<OrderStatus, string> = {
  [OrderStatus.WAITING_FOR_PAYMENT]: "等待买家付款",
  [OrderStatus.PAYMENT_SUCCESS]: "付款成功，待发货",
  [OrderStatus.PAYMENT_CLOSED]: "支付关闭",
  [OrderStatus.SHIPPED]: "已发货",
  [OrderStatus.DELIVERED]: "已收货",
  [OrderStatus.EXCHANGED]: "已换货",
  [OrderStatus.EXCHANGE_SHIPPED]: "换货已发货",
  [OrderStatus.REFUND_CLOSED]: "交易关闭",
  [OrderStatus.TRANSACTION_SUCCESS]: "交易成功",
  [OrderStatus.ORDER_EXCEPTION]: "异常订单",
  [OrderStatus.REDEMPTION_EXCEPTION]: "异常订单",
  [OrderStatus.INTEGRAL_REDEMPTION_EXCEPTION]: "异常订单",
  [OrderStatus.TIMEOUT_EXCEPTION]: "异常订单",
  [OrderStatus.DELETED]: "已删除",
};

export function useOrderStatus() {
  /**
   * 是否含有虚拟商品
   * - 0：无；
   * - 1：含有；
   * - 2：全部
   */
  const getVirtualGoodsType = (orderInfo: OrderItemResult) => {
    let virtualCount = 0;
    let listLength = orderInfo.goodsOrderResultList.length;
    orderInfo.goodsOrderResultList.forEach((item) => {
      // 这么写的原因是存在一个商品就是实物+虚拟商品的情况
      if (item.goodsType == 1) {
        virtualCount += 1;
      }
      if (item.goodsType == 2) {
        virtualCount += 1;
        listLength += 1;
      }
    });
    if (virtualCount === 0) {
      return 0; // 没有虚拟商品
    } else if (virtualCount === listLength) {
      return 2; // 全部虚拟商品
    } else {
      return 1; // 部分虚拟商品
    }
  };

  /**
   * 是否纯兑换订单
   * - 0：无；
   * - 1：含有；
   * - 2：全部
   */
  const getRedemptionGoodsType = (orderInfo: OrderItemResult) => {
    let redemptionCount = 0;
    let listLength = orderInfo.goodsOrderResultList.length;
    orderInfo.goodsOrderResultList.forEach((item) => {
      // 商品分三种：积分+钱、纯积分、纯钱(支持0元下单)；只有纯积分兑换(积分价格>0 且 price==0)才算兑换商品，
      // 积分+钱组合不算纯兑换，纯钱支付的商品也可能0元下单，不能仅用 price == 0 判断
      if (item.integralPrice > 0 && item.price == 0) {
        redemptionCount += 1;
      }
    });
    if (redemptionCount === 0) {
      return 0; // 没有兑换商品
    } else if (redemptionCount === listLength) {
      return 2; // 全部兑换商品
    } else {
      return 1; // 含有兑换商品
    }
  };

  /**
   * 计算下单返奖励金到账状态
   * - 交易成功：已到账
   * - 交易关闭：已退回
   * - 付款成功/已发货：
   *   - 已到账或客服提前申请到账：已到账
   *   - 未到账：支付时间 + 15天 - 当前时间
   * @param orderInfo 订单信息
   * @param currentTime 当前时间戳，由调用方传入（通常来自各自的 CountdownController 实例）
   * @param options 配置项，用于控制是否展示交易成功/交易关闭等终态的到账结果
   */
  const getIntegralArrivalState = (orderInfo: OrderItemResult, currentTime: number, options: IntegralArrivalOptions = {}): IntegralArrivalState => {
    const { orderStatus, isPresentedIntegralIssued, payDate, presentedIntegral } = orderInfo;
    // 赠送积分为0，不展示
    if (presentedIntegral == 0) return { type: "none" };
    // 交易成功
    if (options.showTerminalStateArrival && orderStatus === OrderStatus.TRANSACTION_SUCCESS) return { type: "arrived" };
    // 交易关闭
    if (options.showTerminalStateArrival && orderStatus === OrderStatus.REFUND_CLOSED) return { type: "returned" };
    // 兑换成功，不展示
    if (orderStatus === OrderStatus.PAYMENT_SUCCESS && getRedemptionGoodsType(orderInfo) == 2) return { type: "none" };
    // 仅付款成功 / 已发货状态需要计算到账倒计时
    if (orderStatus !== OrderStatus.PAYMENT_SUCCESS && orderStatus !== OrderStatus.SHIPPED) return { type: "none" };

    // 已到账或客服提前申请到账
    if (isPresentedIntegralIssued) return { type: "arrived" };

    const arrivalTime = payDate + 15 * 24 * 60 * 60 * 1000;
    // 已到账
    if (arrivalTime - currentTime <= 0) return { type: "arrived" };

    const time = DateHelper.formatDate(arrivalTime, "HH:mm");
    // 通过日期对比判断今天/明天/后天，而非剩余时间差
    const dayDiff = DateHelper.formatTotalDays(new Date(currentTime), new Date(arrivalTime));
    const dayLabel = INTEGRAL_ARRIVAL_DAY_LABEL[dayDiff];
    return { type: "pending", dayLabel, time, dayDiff };
  };

  const getOrderStatusLabel = (orderInfo: OrderItemResult): string => {
    if (!orderInfo) return "";

    // 兑换成功：仅有积分兑换商品，且订单状态为付款成功
    if (getRedemptionGoodsType(orderInfo) == 2 && orderInfo.orderStatus == OrderStatus.PAYMENT_SUCCESS) {
      return "兑换成功";
    }
    // 已发放：全部为虚拟商品，且不含积分兑换
    if (getVirtualGoodsType(orderInfo) == 2 && getRedemptionGoodsType(orderInfo) == 0 && orderInfo.orderStatus == OrderStatus.PAYMENT_SUCCESS) {
      return "已发放";
    }

    return OrderStatusLabel[orderInfo.orderStatus] || "";
  };

  const getOrderStatusStyle = (orderInfo: OrderItemResult): string => {
    // 返回三种颜色标识： "gray" | "orange" | "green"
    if (!orderInfo) return "";

    // 兑换成功：仅有积分兑换商品，且订单状态为付款成功
    if (getRedemptionGoodsType(orderInfo) == 2 && orderInfo.orderStatus == OrderStatus.PAYMENT_SUCCESS) {
      return "green";
    }

    // 已发放：全部为虚拟商品，且不含积分兑换
    if (getVirtualGoodsType(orderInfo) == 2 && getRedemptionGoodsType(orderInfo) == 0 && orderInfo.orderStatus == OrderStatus.PAYMENT_SUCCESS) {
      return "green";
    }

    const graySet = new Set<OrderStatus>([OrderStatus.PAYMENT_CLOSED, OrderStatus.REFUND_CLOSED, OrderStatus.DELETED]);
    const redSet = new Set<OrderStatus>([OrderStatus.ORDER_EXCEPTION, OrderStatus.REDEMPTION_EXCEPTION, OrderStatus.INTEGRAL_REDEMPTION_EXCEPTION, OrderStatus.TIMEOUT_EXCEPTION]);
    const greenSet = new Set<OrderStatus>([OrderStatus.SHIPPED, OrderStatus.DELIVERED, OrderStatus.TRANSACTION_SUCCESS]);

    if (graySet.has(orderInfo.orderStatus)) return "gray";
    if (greenSet.has(orderInfo.orderStatus)) return "green";
    if (redSet.has(orderInfo.orderStatus)) return "red";

    // 兜底 orange
    return "orange";
  };

  /**
   * 判断订单状态是否支持发起退款
   * 支持的状态：
   * - 异常类：ORDER_EXCEPTION、INTEGRAL_REDEMPTION_EXCEPTION、REDEMPTION_EXCEPTION、TIMEOUT_EXCEPTION
   * - 成交类：TRANSACTION_SUCCESS
   * 返回 true 表示该状态允许申请退款
   */
  const isRefundSupportedStatus = (orderStatus: OrderStatus): boolean => {
    const refundableStatuses = new Set<OrderStatus>([
      OrderStatus.ORDER_EXCEPTION,
      OrderStatus.INTEGRAL_REDEMPTION_EXCEPTION,
      OrderStatus.TIMEOUT_EXCEPTION,
      OrderStatus.REDEMPTION_EXCEPTION,
      OrderStatus.TRANSACTION_SUCCESS,
    ]);
    return refundableStatuses.has(orderStatus);
  };

  return { getOrderStatusLabel, getOrderStatusStyle, isRefundSupportedStatus, getRedemptionGoodsType, getIntegralArrivalState };
}
